import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, Button, Grid, IconButton, Paper, Popover, Stack, TextField, Typography, } from "@mui/material";
import React from "react";
import { ColorSwatchPicker } from "../utils/ColorSwatchPicker";
import { Controller, useFormContext } from "react-hook-form";
import { sortPlayers } from "../players/PlayerUtils";
import { isPlayerSummary, playerSummaryName } from "../players/utils";
import { TeamColorBox } from "../teams/TeamColorBox";
import { CountChip } from "../utils/CountChip";
export const TOURNAMENT_TEAM_COLOR_RED = { hex: "#D32F2F", name: "Red" };
export const TOURNAMENT_TEAM_COLOR_BLUE = { hex: "#1976D2", name: "Blue" };
export const TOURNAMENT_TEAM_COLOR_WHITE = { hex: "#FFFFFF", name: "White" };
const TOURNAMENT_TEAM_COLORS = [
    TOURNAMENT_TEAM_COLOR_RED,
    TOURNAMENT_TEAM_COLOR_BLUE,
    TOURNAMENT_TEAM_COLOR_WHITE,
    { hex: "#000000", name: "Black" },
    { hex: "#4CAF50", name: "Green" },
    { hex: "#FBC02D", name: "Yellow" },
    { hex: "#26C6DA", name: "Sky Blue" },
    { hex: "#78909C", name: "Dark Gray" },
];
// A single picker row: name (greyed for a departed player) with a trailing
// control slot, zebra-striped by row index for readability in long squads.
const PlayerPickerRow = ({ index, name, departed = false, children, }) => (_jsxs(Box, { sx: {
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        flexWrap: "wrap",
        px: 1,
        py: 0.75,
        borderRadius: 1,
        bgcolor: index % 2 === 1 ? "action.hover" : "transparent",
    }, children: [departed && (
        // titleAccess exposes the status to screen readers (a plain Tooltip never
        // fires on touch); the "(former)" suffix puts it in the accessible name
        // too, so the status isn't conveyed by colour/icon alone.
        _jsx(PersonOffIcon, { fontSize: "small", sx: { color: "error.main" }, titleAccess: "No longer on the team" })), _jsxs(Typography, { sx: { flexGrow: 1, minWidth: 100 }, noWrap: true, color: departed ? "text.disabled" : undefined, children: [name, departed ? " (former)" : ""] }), children] }));
export const TeamChooser = ({ teamSelection, setTeamSelection, canEditTeamNames, minTeams = 2, maxTeams = TOURNAMENT_TEAM_COLORS.length, onRemoveDeparted, }) => {
    // Move a player to a team (by index) or to the bench. A player belongs to one
    // place, so they're removed from everywhere else first. Departed players
    // (summary only) never sit on the bench — their removal is confirmed upstream.
    const movePlayer = (player, target) => {
        const updatedTeams = teamSelection.teams.map((team) => ({
            ...team,
            players: team.players.filter((p) => p.id !== player.id),
        }));
        const updatedBench = teamSelection.bench.filter((p) => p.id !== player.id);
        if (target === "bench") {
            if ("user" in player) {
                updatedBench.push(player);
            }
        }
        else {
            updatedTeams[target] = {
                ...updatedTeams[target],
                players: [...updatedTeams[target].players, player],
            };
        }
        setTeamSelection({ teams: updatedTeams, bench: updatedBench });
    };
    const handleColorChange = (index, color) => {
        const updatedTeams = [...teamSelection.teams];
        updatedTeams[index] = { ...updatedTeams[index], primaryColour: color };
        setTeamSelection({ ...teamSelection, teams: updatedTeams });
        setColorAnchor(null);
    };
    const [colorAnchor, setColorAnchor] = React.useState(null);
    const handleClickColorPicker = (event, index) => {
        setColorAnchor({ anchorEl: event.currentTarget, teamIndex: index });
    };
    const handleCloseColorPicker = () => setColorAnchor(null);
    const handleRemoveTeam = (index) => {
        const updatedTeams = [...teamSelection.teams];
        updatedTeams.splice(index, 1);
        setTeamSelection({ ...teamSelection, teams: updatedTeams });
    };
    const handleAddTeam = () => {
        if (teamSelection.teams.length >= maxTeams)
            return;
        const newTeam = {
            name: `${TOURNAMENT_TEAM_COLORS[teamSelection.teams.length].name} Team`,
            players: [],
            primaryColour: TOURNAMENT_TEAM_COLORS[teamSelection.teams.length].hex,
        };
        setTeamSelection({
            ...teamSelection,
            teams: [...teamSelection.teams, newTeam],
        });
    };
    const handleNameChange = (index, name) => {
        const updatedTeams = [...teamSelection.teams];
        updatedTeams[index] = { ...updatedTeams[index], name };
        setTeamSelection({ ...teamSelection, teams: updatedTeams });
    };
    const { control, formState: { errors }, } = useFormContext();
    const sortedBenchPlayers = React.useMemo(() => {
        return [...teamSelection.bench].sort(sortPlayers);
    }, [teamSelection.bench]);
    return (_jsxs(Grid, { container: true, spacing: 2, alignItems: "flex-start", children: [_jsx(Grid, { item: true, xs: 12, md: 8, children: _jsx(Box, { sx: { maxHeight: "80vh", overflowY: "auto", pr: { md: 2 } }, children: _jsxs(Grid, { container: true, spacing: 2, children: [teamSelection.teams.map((team, index) => (_jsx(Grid, { item: true, xs: 12, sm: teamSelection.teams.length === 1 ? 12 : 6, children: _jsxs(Paper, { variant: "outlined", sx: { p: 2, mb: 2 }, children: [_jsxs(Box, { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, sx: { mb: 1 }, children: [canEditTeamNames && (_jsxs(_Fragment, { children: [_jsx(TeamColorBox, { color: team.primaryColour, onClick: (e) => handleClickColorPicker(e, index) }), _jsx(Popover, { open: !!colorAnchor, anchorEl: colorAnchor?.anchorEl, onClose: handleCloseColorPicker, anchorOrigin: {
                                                                vertical: "bottom",
                                                                horizontal: "left",
                                                            }, children: _jsx(Box, { sx: { p: 1.5, maxWidth: 240 }, children: _jsx(ColorSwatchPicker, { colors: TOURNAMENT_TEAM_COLORS.map((c) => c.hex), value: team.primaryColour, onChange: (hex) => {
                                                                        if (!colorAnchor)
                                                                            return;
                                                                        handleColorChange(colorAnchor.teamIndex, hex);
                                                                    } }) }) })] })), canEditTeamNames ? (_jsx(Controller, { name: `intraSquadTournamentTeams.${index}.name`, control: control, rules: { required: "Team name is required" }, render: ({ field }) => (_jsx(TextField, { ...field, variant: "standard", fullWidth: true, onChange: (e) => handleNameChange(index, e.target.value), inputProps: { "aria-label": team.name }, error: !!errors.intraSquadTournamentTeams?.[index], helperText: errors.intraSquadTournamentTeams?.[index]?.name?.message })) })) : (_jsx(Typography, { variant: "body1", color: "text.secondary", sx: { flexGrow: 1 }, children: team.name })), _jsx(CountChip, { count: team.players.length }), canEditTeamNames &&
                                                    teamSelection.teams.length > minTeams && (_jsx(IconButton, { onClick: () => handleRemoveTeam(index), "aria-label": "remove team", children: _jsx(RemoveIcon, {}) }))] }), team.players.length === 0 ? (_jsx(Typography, { variant: "body2", color: "text.secondary", children: "No players yet." })) : (_jsx(Stack, { spacing: 0, children: team.players.map((player, rowIndex) => {
                                                const departed = isPlayerSummary(player);
                                                const name = playerSummaryName(player);
                                                return (_jsx(PlayerPickerRow, { index: rowIndex, name: name, departed: departed, children: _jsx(IconButton, { size: "small", "aria-label": departed
                                                            ? `Remove ${name} from match`
                                                            : `Remove ${name} from ${team.name}`, onClick: () => departed
                                                            ? onRemoveDeparted?.(player)
                                                            : movePlayer(player, "bench"), children: _jsx(CloseIcon, { fontSize: "small" }) }) }, player.id));
                                            }) }))] }) }, `team${index}`))), canEditTeamNames && teamSelection.teams.length < maxTeams && (_jsx(Grid, { item: true, xs: 12, sm: 6, children: _jsxs(Box, { onClick: handleAddTeam, sx: {
                                        border: "2px dashed grey",
                                        minHeight: "120px",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        cursor: "pointer",
                                        borderRadius: 1,
                                        textAlign: "center",
                                        p: 2,
                                        mb: 2,
                                    }, children: [_jsx(AddIcon, { fontSize: "large", sx: { mb: 1 } }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "Add another team" })] }) }, "addTeam"))] }) }) }), _jsx(Grid, { item: true, xs: 12, md: 4, children: _jsxs(Box, { sx: { maxHeight: "80vh", overflowY: "auto" }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, mb: 1 }, children: [_jsx(Typography, { variant: "body1", color: "text.secondary", "aria-label": "bench", children: "Bench" }), _jsx(CountChip, { count: sortedBenchPlayers.length })] }), sortedBenchPlayers.length === 0 ? (_jsx(Typography, { variant: "body2", color: "text.secondary", children: "All players assigned." })) : (_jsx(Stack, { spacing: 0, children: sortedBenchPlayers.map((player, rowIndex) => {
                                const name = playerSummaryName(player);
                                return (_jsx(PlayerPickerRow, { index: rowIndex, name: name, children: _jsx(Stack, { direction: "row", spacing: 0.5, flexWrap: "wrap", useFlexGap: true, children: teamSelection.teams.map((team, index) => (_jsx(Button, { size: "small", variant: "outlined", onClick: () => movePlayer(player, index), "aria-label": `Assign ${name} to ${team.name}`, children: team.name }, `assign-${index}`))) }) }, player.id));
                            }) }))] }) })] }));
};
